import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import api, { unwrap } from '../utils/api';

export default function Checkout() {
  const nav = useNavigate();
  const { cart, removeItem, applyCoupon, refresh } = useCart();
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [processing, setProcessing] = useState(false);
  const [discountPercent, setDiscountPercent] = useState(0);

  const subtotal = (cart.items || []).reduce((s, i) => s + Number(i.price || 0), 0);
  const discount = Math.round(subtotal * (discountPercent / 100) * 100) / 100;
  const total = Math.max(0, subtotal - discount);
  const isFree = discountPercent >= 100 || total <= 0;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  async function onApply() {
    setMsg(''); setErr('');
    try {
      const r = await applyCoupon(code);
      setDiscountPercent(Number(r.coupon.discountPercent) || 0);
      if (Number(r.coupon.discountPercent) >= 100) {
        setMsg(`Coupon ${r.coupon.couponCode} applied — your order is FREE!`);
      } else {
        setMsg(`Coupon ${r.coupon.couponCode} applied (${r.coupon.discountPercent}% off)`);
      }
    } catch (_) {
      setDiscountPercent(0);
      setErr('Invalid coupon');
    }
  }

  async function onPay() {
    setErr('');
    if (!emailValid) {
      setErr('Please enter a valid email address before continuing.');
      return;
    }
    setProcessing(true);
    try {
      if (isFree) {
        // 100%-off path: skip Stripe, create a free order, and go straight
        // to the download page. The backend emails the receipt on this path.
        const r = await api.post('/cart/process-free-order', { email });
        const order = unwrap(r);
        refresh();
        nav(`/checkout/success?order_id=${order._id}&token=${encodeURIComponent(order.downloadToken)}`);
        return;
      }
      const r = await api.post('/cart/create-checkout-session', { email });
      const body = unwrap(r);
      if (body?.url) {
        window.location.href = body.url;
      } else {
        setErr('Could not start checkout. Please try again.');
      }
    } catch (e) {
      const m = e?.response?.data?.body;
      setErr(typeof m === 'string' ? m : 'Payment provider is not configured yet. Please contact support.');
    } finally {
      setProcessing(false);
    }
  }

  return (
    <div style={{ maxWidth: 760, margin: '40px auto', padding: '0 20px' }}>
      <h1>Checkout</h1>
      {(!cart.items || cart.items.length === 0) ? (
        <p>Your cart is empty.</p>
      ) : (
        <>
          <table className="cart-table">
            <thead><tr><th>Template</th><th>Price</th><th /></tr></thead>
            <tbody>
              {cart.items.map(i => (
                <tr key={i._id}>
                  <td>{i.productId?.name || String(i.productId).slice(-6)}</td>
                  <td>${Number(i.price || 0).toFixed(2)}</td>
                  <td><button onClick={() => removeItem(i._id)} style={{ background: 'none', border: 'none', color: '#dc3545' }}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input placeholder="Coupon code" value={code} onChange={e => setCode(e.target.value)} style={{ flex: 1, padding: 10, border: '1px solid #ddd', borderRadius: 8 }} />
            <button className="btn-outline" onClick={onApply}>Apply</button>
          </div>
          {msg && <p className="success-text">{msg}</p>}

          <div style={{ borderTop: '1px solid #eee', paddingTop: 12, marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, color: '#555' }}>
              <span>Subtotal</span><span>${subtotal.toFixed(2)}</span>
            </div>
            {discount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, color: '#0a7a32', marginTop: 4 }}>
                <span>Discount ({discountPercent}% off)</span><span>−${discount.toFixed(2)}</span>
              </div>
            )}
            <h3 style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
              <span>Total</span>
              <span>{isFree ? 'FREE' : `$${total.toFixed(2)}`}</span>
            </h3>
          </div>

          <div style={{ marginTop: 20, marginBottom: 14 }}>
            <label style={{ display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
              Email {isFree ? '(we’ll send your download link here)' : '(for your receipt)'}
            </label>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={{ width: '100%', padding: 12, border: '1px solid #ddd', borderRadius: 8 }}
            />
          </div>

          {err && <p className="error-text">{err}</p>}
          <button
            className="btn-primary"
            style={{ width: '100%', opacity: !emailValid || processing ? 0.55 : 1, cursor: !emailValid || processing ? 'not-allowed' : 'pointer' }}
            onClick={onPay}
            disabled={processing || !emailValid}
          >
            {processing
              ? (isFree ? 'Preparing your invitation…' : 'Redirecting to secure checkout…')
              : (isFree ? 'Get my invitation' : 'Pay with card')}
          </button>
        </>
      )}
    </div>
  );
}
